import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GraduationCap, FileText, Calculator, Percent, ArrowRight, User, Users, Loader2 } from "lucide-react";

const OPTION_META = {
  application: { icon: FileText, desc: "Start your education loan application in minutes." },
  calculator: { icon: Calculator, desc: "Estimate your EMI, tenure and eligibility." },
  interest: { icon: Percent, desc: "See today's latest education loan interest rates." },
};
const PROFESSIONS = ["Salaried - Private", "Salaried - Government", "Self-Employed / Business", "Doctor", "Farmer / Agriculture", "Retired", "Homemaker", "Other"];
const digits = (m) => String(m || "").replace(/\D/g, "");
const empty = { student_name: "", student_mobile: "", student_email: "", course: "", city: "", parent_name: "", parent_mobile: "", parent_profession: "", parent_profession_details: "" };

export default function EducationFair() {
  const [options, setOptions] = useState([]);
  const [active, setActive] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState(empty);

  useEffect(() => { api.get("/public/edu/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const activeLabel = options.find((o) => o.id === active)?.label || "";

  const open = (id) => { setActive(id); setF(empty); };
  const submit = async () => {
    if (!f.student_name.trim() || !f.student_mobile.trim()) return toast.error("Please enter the student's name and mobile");
    if (digits(f.student_mobile).length < 10) return toast.error("Enter a valid 10-digit mobile number");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/edu-lead", { option: active, ...f });
      toast.success("Thank you! Taking you there…");
      setTimeout(() => { window.location.href = data.redirect_url; }, 700);
    } catch (e) {
      setSubmitting(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#0A192F] text-white flex flex-col" data-testid="edu-fair-page">
      {/* Hero */}
      <div className="px-6 pt-10 pb-8 text-center bg-gradient-to-b from-[#0F4C81] to-[#0A192F]">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/20 mb-4">
          <GraduationCap size={32} className="text-emerald-400" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">DIGI-LOANS Education Loans</h1>
        <p className="text-slate-300 mt-2 text-sm sm:text-base max-w-md mx-auto">Welcome to the Education Fair! Choose an option below to get started.</p>
      </div>

      {/* Options */}
      <div className="flex-1 px-5 py-8 max-w-lg w-full mx-auto space-y-4">
        {options.map((o) => {
          const meta = OPTION_META[o.id] || { icon: FileText, desc: "" };
          const Icon = meta.icon;
          return (
            <button
              key={o.id}
              onClick={() => open(o.id)}
              data-testid={`edu-option-${o.id}`}
              className="group w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-400/50 rounded-2xl p-5 flex items-center gap-4 transition-all"
            >
              <div className="shrink-0 w-12 h-12 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Icon size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-base sm:text-lg">{o.label}</div>
                <div className="text-xs text-slate-400 mt-0.5">{meta.desc}</div>
              </div>
              <ArrowRight size={20} className="text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all shrink-0" />
            </button>
          );
        })}
        {options.length === 0 && <div className="text-center text-slate-400 py-10">Loading options…</div>}
      </div>

      <div className="text-center text-xs text-slate-500 pb-6 px-6">DIGI-LOANS · Your details are captured securely to help our team assist you faster.</div>

      {/* Capture form */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="edu-capture-dialog">
          <DialogHeader>
            <DialogTitle>{activeLabel}</DialogTitle>
            <DialogDescription>Please share a few details before we continue. Fields marked * are required.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-2"><User size={13} />Student Details</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="mb-1.5 block">Student Name *</Label><Input data-testid="edu-student-name" value={f.student_name} onChange={(e) => setF({ ...f, student_name: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Student Mobile *</Label><Input data-testid="edu-student-mobile" inputMode="numeric" value={f.student_mobile} onChange={(e) => setF({ ...f, student_mobile: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Email</Label><Input data-testid="edu-student-email" value={f.student_email} onChange={(e) => setF({ ...f, student_email: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Course / Program</Label><Input data-testid="edu-course" value={f.course} onChange={(e) => setF({ ...f, course: e.target.value })} placeholder="e.g. B.Tech, MBBS, MBA" /></div>
                <div><Label className="mb-1.5 block">City</Label><Input data-testid="edu-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-2"><Users size={13} />Parent / Guardian Details</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1.5 block">Parent Name</Label><Input data-testid="edu-parent-name" value={f.parent_name} onChange={(e) => setF({ ...f, parent_name: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Parent Mobile</Label><Input data-testid="edu-parent-mobile" inputMode="numeric" value={f.parent_mobile} onChange={(e) => setF({ ...f, parent_mobile: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label className="mb-1.5 block">Parent Profession</Label>
                  <Select value={f.parent_profession} onValueChange={(v) => setF({ ...f, parent_profession: v })}>
                    <SelectTrigger data-testid="edu-parent-profession"><SelectValue placeholder="Select profession" /></SelectTrigger>
                    <SelectContent>{PROFESSIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label className="mb-1.5 block">Profession Details</Label><Input data-testid="edu-parent-profession-details" value={f.parent_profession_details} onChange={(e) => setF({ ...f, parent_profession_details: e.target.value })} placeholder="Employer / business name, designation, monthly income" /></div>
              </div>
            </div>

            <Button onClick={submit} disabled={submitting} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-base" data-testid="edu-submit-btn">
              {submitting ? <><Loader2 size={18} className="mr-2 animate-spin" />Redirecting…</> : <>Continue <ArrowRight size={18} className="ml-2" /></>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
